// SundayDinnerRail — Home-page headliner for the Recipes section.
//
// Pulls the top 8-10 recipes from /api/recipes and renders them as a
// horizontal-scrolling rail of glossy cards. Each card shows the dish
// name, cook time, and a YouTube tutorial badge. Tap → full recipe
// with embedded video + read-aloud cooking instructions.
//
// Placement: rendered on Home.jsx between Trending and the rest of
// the rails. Headlining the section because Sunday dinner is the
// section the founder asked us to spotlight (Feb 2026).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ForkKnife, Clock, YoutubeLogo, ArrowRight, Sparkle } from "@phosphor-icons/react";
import api from "../lib/api";
import { useAudio } from "../context/AudioContext";
import Tr from "./Tr";

export default function SundayDinnerRail() {
  // Active UI language so the card titles + intros come back
  // pre-translated from the backend.
  const { language } = useAudio();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = language && language !== "en" ? `&lang=${language}` : "";
    api.get(`/recipes?limit=12${qs}`)
      .then((r) => { if (!cancelled) setRecipes(r.data?.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [language]);

  if (loading || recipes.length === 0) return null;

  return (
    <section className="mt-16 md:mt-20" data-testid="sunday-dinner-rail">
      <div className="flex items-end justify-between mb-5 px-1">
        <div>
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-terracotta font-bold">
            <Sparkle size={12} weight="fill" /> <Tr>Sunday Dinner</Tr>
          </span>
          <h2 className="font-heading text-3xl md:text-5xl text-forest font-medium mt-1.5 flex items-center gap-3">
            <ForkKnife size={28} weight="duotone" className="text-terracotta md:hidden" />
            <Tr>Soul food recipes</Tr>
          </h2>
          <p className="text-sm md:text-base text-muted_ink mt-2 max-w-xl">
            <Tr>Big Mama's classics with step-by-step video tutorials right in the app. Read recipes aloud while you cook — hands-free.</Tr>
          </p>
        </div>
        <Link
          to="/recipes"
          data-testid="sunday-dinner-rail-see-all"
          className="hidden md:inline-flex items-center gap-1 text-sm font-bold text-forest hover:text-terracotta whitespace-nowrap"
        >
          <Tr>See all</Tr> <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      <div
        className="-mx-5 md:-mx-12 px-5 md:px-12 flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory hw-no-scrollbar"
        data-testid="sunday-dinner-rail-scroll"
      >
        {recipes.map((r) => (
          <Link
            key={r.id}
            to={`/recipes/${r.id}`}
            data-testid={`sunday-dinner-card-${r.id}`}
            // Solid theme-aware bg replaces the cream gradient — the
            // gradient color-stops bypassed theme.css's midnight/OLED
            // overrides so OLED-mode users saw light-on-light text.
            // bg-cream-200 IS overridden by theme.css in midnight to
            // a dark surface with cream text, so the card now flips
            // properly. Feb 2026 owner-reported readability fix.
            className="snap-start shrink-0 w-[170px] md:w-[200px] rounded-2xl bg-cream-200 border border-cream-300 hover:border-forest hover:shadow-lg transition-all overflow-hidden group"
          >
            {/* Top "stripe" with kitchen-y warmth — a small terracotta
                strip with a fork-knife icon, gives the card a recipe-
                card visual identity without needing photography. */}
            <div className="h-2 bg-gradient-to-r from-terracotta to-[#d97757]" />
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <ForkKnife size={20} weight="duotone" className="text-terracotta" />
                {r.youtube_url && (
                  <YoutubeLogo size={18} weight="fill" className="text-red-600" />
                )}
              </div>
              <h3
                className="font-heading text-lg text-forest leading-tight group-hover:text-terracotta transition-colors line-clamp-2 min-h-[2.6rem] font-semibold"
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.15)" }}
              >
                {r.title}
              </h3>
              <p
                className="text-xs text-muted_ink mt-2 line-clamp-2 font-medium"
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.15)" }}
              >
                {r.intro}
              </p>
              {r.cook_time && (
                <div className="mt-3 inline-flex items-center gap-1 text-[10px] text-muted_ink font-semibold">
                  <Clock size={11} weight="bold" /> {r.cook_time}
                </div>
              )}
            </div>
          </Link>
        ))}
        {/* Final "See all" tile, mobile-only — gives the rail a clean
            terminus rather than fading off-screen. */}
        <Link
          to="/recipes"
          data-testid="sunday-dinner-rail-see-all-tile"
          className="md:hidden snap-start shrink-0 w-[160px] rounded-2xl bg-forest text-cream flex flex-col items-center justify-center p-4 gap-2 hover:bg-forest-600 transition-all"
        >
          <ForkKnife size={28} weight="duotone" />
          <span className="text-sm font-bold text-center">
            <Tr>See all recipes</Tr>
          </span>
          <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </section>
  );
}
